/**
 * Form validation utility functions
 */
import { parseDate } from './formatters';

/**
 * Validate email address format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid email format
 */
export const validateEmail = (email) => {
  if (!email) return false;

  // RFC 5322 simplified regex for email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate password strength
 * Requirements: At least 8 characters
 * @param {string} password - Password to validate
 * @returns {boolean} - True if password meets requirements
 */
export const validatePassword = (password) => {
  if (!password) return false;

  // At least 8 characters
  return password.length >= 8;
};

/**
 * Get detailed password validation feedback
 * @param {string} password - Password to validate
 * @returns {Object} - Object with isValid boolean and errors array
 */
export const getPasswordValidation = (password) => {
  const errors = [];

  if (!password) {
    errors.push('Password is required');
    return { isValid: false, errors };
  }

  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }

  if (password.length > 128) {
    errors.push('Password must be less than 128 characters');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate phone number format
 * Accepts formats: (555) 123-4567, 555-123-4567, 5551234567
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid phone format
 */

export const validatePhone = (phone) => {
  if (!phone) return false;

  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // US phone numbers are 10 digits
  return cleaned.length === 10;
};

/**
 * Validate required field is not empty
 * @param {*} value - Value to validate
 * @returns {boolean} - True if value is not empty
 */
export const validateRequired = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * Validate minimum length
 * @param {string} value - String value to validate
 * @param {number} minLength - Minimum required length
 * @returns {boolean} - True if meets minimum length
 */
export const validateMinLength = (value, minLength) => {
  if (!value) return false;
  return value.length >= minLength;
};

/**
 * Validate maximum length
 * @param {string} value - String value to validate
 * @param {number} maxLength - Maximum allowed length
 * @returns {boolean} - True if within maximum length
 */
export const validateMaxLength = (value, maxLength) => {
  if (!value) return true; // Empty is valid for max length check
  return value.length <= maxLength;
};

/**
 * Validate numeric value
 * @param {*} value - Value to validate
 * @returns {boolean} - True if value is a number
 */
export const validateNumber = (value) => {
  if (value === null || value === undefined || value === '') return false;
  return !isNaN(parseFloat(value)) && isFinite(value);
};

/**
 * Validate positive number
 * @param {*} value - Value to validate
 * @returns {boolean} - True if value is a positive number
 */
export const validatePositiveNumber = (value) => {
  if (!validateNumber(value)) return false;
  return parseFloat(value) > 0;
};

/**
 * Validate date is in the future
 * @param {string|Date} dateValue - Date to validate
 * @returns {boolean} - True if date is in the future
 */
export const validateFutureDate = (dateValue) => {
  if (!dateValue) return false;

  try {
    const date = parseDate(dateValue);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day

    return date >= today;
  } catch {
    return false;
  }
};

/**
 * Validate ZIP code format (US)
 * Accepts: 12345 or 12345-6789
 * @param {string} zip - ZIP code to validate
 * @returns {boolean} - True if valid ZIP format
 */
export const validateZipCode = (zip) => {
  if (!zip) return false;

  // 5 digits or 5+4 format
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zip);
};

/**
 * Validate form object with multiple fields
 * @param {Object} formData - Form data object
 * @param {Object} rules - Validation rules object
 * @returns {Object} - Object with isValid boolean and errors object
 *
 * Example usage:
 * const rules = {
 *   email: { required: true, email: true },
 *   password: { required: true, minLength: 8 }
 * };
 * const result = validateForm(formData, rules);
 */
export const validateForm = (formData, rules) => {
  const errors = {};
  let isValid = true;

  Object.keys(rules).forEach((field) => {
    const fieldRules = rules[field];
    const value = formData[field];
    const fieldErrors = [];

    if (fieldRules.required && !validateRequired(value)) {
      fieldErrors.push('This field is required');
    }

    if (fieldRules.email && value && !validateEmail(value)) {
      fieldErrors.push('Please enter a valid email address');
    }

    if (fieldRules.phone && value && !validatePhone(value)) {
      fieldErrors.push('Please enter a valid phone number');
    }

    if (fieldRules.minLength && value && !validateMinLength(value, fieldRules.minLength)) {
      fieldErrors.push(`Must be at least ${fieldRules.minLength} characters`);
    }

    if (fieldRules.maxLength && value && !validateMaxLength(value, fieldRules.maxLength)) {
      fieldErrors.push(`Must be less than ${fieldRules.maxLength} characters`);
    }

    if (fieldRules.number && value && !validateNumber(value)) {
      fieldErrors.push('Must be a valid number');
    }

    if (fieldRules.positive && value && !validatePositiveNumber(value)) {
      fieldErrors.push('Must be a positive number');
    }

    if (fieldRules.futureDate && value && !validateFutureDate(value)) {
      fieldErrors.push('Date must be today or in the future');
    }

    if (fieldRules.zipCode && value && !validateZipCode(value)) {
      fieldErrors.push('Please enter a valid ZIP code');
    }

    if (fieldRules.pattern && value && !fieldRules.pattern.test(value)) {
      fieldErrors.push(fieldRules.patternMessage || 'Invalid format');
    }

    if (fieldErrors.length > 0) {
      errors[field] = fieldErrors;
      isValid = false;
    }
  });

  return { isValid, errors };
};
